import { NgModule, inject } from '@angular/core';
import { PreloadAllModules, RouterModule, Routes } from '@angular/router';
import { loginGuard } from './core/guards/login.guard';
import { LimitedAppPage } from './limited-app/limited-app.page';
import { UnrecoverableErrorPage } from './unrecoverable-error/unrecoverable-error.page';
import { isAuthenticatedGuard } from './core/guards/is-authenticated.guard';
import { AuthenticationService } from './core/services/authentication.service';
import { map } from 'rxjs';
import { Route } from './core/enums/route.enum';
import { validateInvitationGuard } from './invitation/validate-invitation.guard';
import { impersonationGuard } from './impersonation/impersonation.guard';
import { accountStateGuard } from './core/guards/account-state.guard';
import { rootNavigationGuard } from './core/guards/root-navigation.guard';

const routes: Routes = [
  {
    path: Route.Home,
    canActivate: [isAuthenticatedGuard, accountStateGuard],
    loadChildren: () => import('./home/home.routes').then((m) => m.homeRoutes)
  },
  {
    path: Route.Login,
    loadChildren: () => import('./login/login.module').then((m) => m.LoginModule),
    canActivate: [loginGuard]
  },
  {
    path: Route.Public,
    loadChildren: () => import('./unauthenticated/unauthenticated.routes').then((m) => m.UNAUTHENTICATED_ROUTES),
  },
  {
    path: Route.Registration,
    canActivate: [isAuthenticatedGuard],
    loadChildren: () => import('./registration/routes/registration.routes').then((m) => m.registrationRoutes),
  },
  {
    path: Route.ShareData,
    canActivate: [isAuthenticatedGuard, rootNavigationGuard],
    loadChildren: () => import('./share-data/routes/share-data.routes').then((m) => m.shareDataRoutes),
  },
  {
    path: Route.Consent,
    canActivate: [isAuthenticatedGuard, rootNavigationGuard],
    loadChildren: () => import('./consent/routes/consent.routes').then((m) => m.consentRoutes),
  },
  {
    path: Route.RegistrationError,
    loadComponent: () =>
      import('./registration-error/registration-error.page').then((m) => m.RegistrationErrorComponent),
    data: { pageTitle: 'RegistrationErrorPage' },
    title: 'Registration error',
  },
  {
    path: Route.Invitation,
    canActivate: [isAuthenticatedGuard, validateInvitationGuard, rootNavigationGuard],
    loadComponent: () => import('./invitation/invitation.page').then((m) => m.InvitationPage),
    data: { pageTitle: 'InvitationPage' },
    title: 'Invitation',
  },
  {
    path: Route.OnboardingWizard,
    loadChildren: () =>
      import('./onboarding/onboarding-wizard.module').then((m) => m.OnboardingWizardModule)
  },
  {
    path: Route.Maintenance,
    loadChildren: () => import('./maintenance/maintenance.module').then((m) => m.MaintenanceModule),
  },
  {
    path: Route.Impersonation,
    canActivate: [impersonationGuard],
    loadComponent: () => import('./impersonation/pages/impersonation.page').then((m) => m.ImpersonationPage),
    data: { pageTitle: 'ImpersonationPage' },
  },
  /**
   * @deprecated Users can, in theory, no longer end in the limited app
   */
  {
    path: Route.LimitedApp,
    component: LimitedAppPage,
    data: { pageTitle: 'LimitedAppPage' },
    title: 'Limited App'
  },
  {
    path: Route.UnrecoverableError,
    component: UnrecoverableErrorPage,
    data: { pageTitle: 'UnrecoverableErrorPage' },
    title: 'Unrecoverable Error'
  },
  {
    path: Route.Logout,
    canActivate: [
      () => {
        const authService = inject(AuthenticationService);
        return authService.authenticated$.pipe(map((isAuthenticated) => isAuthenticated ? authService.logout() : true));
      }
    ],
    children: [],
  },
  {
    path: '',
    redirectTo: Route.Home,
    pathMatch: 'full'
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes, { preloadingStrategy: PreloadAllModules })],
  exports: [RouterModule],
})
export class AppRoutingModule {}
